import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtDateTimeFull } from "@/lib/helpers";
import { MagnifyingGlass, ClockCounterClockwise } from "@phosphor-icons/react";

const ACTION_STYLES = {
  "auth.login": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "auth.login.failed": "bg-red-100 text-red-800 border-red-200",
  "auth.logout": "bg-neutral-100 text-neutral-700 border-neutral-200",
  "student.create": "bg-blue-100 text-blue-800 border-blue-200",
  "student.update": "bg-blue-50 text-blue-700 border-blue-100",
  "invoice.create": "bg-amber-100 text-amber-800 border-amber-200",
  "invoice.payment": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "session.complete": "bg-teal-100 text-teal-800 border-teal-200",
};

const PAGE_SIZE = 50;

export default function AuditLogPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [filter, setFilter] = useState("");
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: append ? skip : 0 };
      if (action) params.action = action;
      const { data } = await api.get("/audit-logs", { params });
      setTotal(data.total);
      setHasMore(data.has_more);
      setSkip((append ? skip : 0) + data.items.length);
      setRows(append ? [...rows, ...data.items] : data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [action, skip, rows]);

  // Reload on action change (reset pagination)
  useEffect(() => {
    setSkip(0); setRows([]);
    (async () => {
      setLoading(true);
      try {
        const params = { limit: PAGE_SIZE, skip: 0 };
        if (action) params.action = action;
        const { data } = await api.get("/audit-logs", { params });
        setTotal(data.total);
        setHasMore(data.has_more);
        setSkip(data.items.length);
        setRows(data.items);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [action]);

  const visible = rows.filter(r => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (r.actor_email || "").toLowerCase().includes(q)
      || (r.action || "").toLowerCase().includes(q)
      || (r.entity_id || "").toLowerCase().includes(q);
  });

  const categories = [
    { key: "", label: "All" },
    { key: "auth", label: "Auth" },
    { key: "student", label: "Students" },
    { key: "session", label: "Sessions" },
    { key: "invoice", label: "Billing" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-1.5">Compliance</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Audit log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every meaningful action across the ERP — who, what, when.
          <span className="text-foreground font-medium ml-2">{total.toLocaleString()} total events</span>
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Tabs value={action} onValueChange={setAction}>
          <TabsList>
            {categories.map(c => (
              <TabsTrigger key={c.key} value={c.key} data-testid={`audit-tab-${c.key || "all"}`}>{c.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative w-full md:w-64 md:ml-auto">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Filter by email / action…" value={filter} onChange={e => setFilter(e.target.value)} className="pl-9" data-testid="audit-filter" />
        </div>
      </div>

      <Card><CardContent className="p-0">
        {loading ? <div className="p-6 text-sm text-muted-foreground">Loading…</div> :
         visible.length === 0 ? (
          <div className="p-10 text-center">
            <ClockCounterClockwise size={32} weight="duotone" className="text-muted-foreground/40 mx-auto mb-2" />
            <div className="text-sm text-muted-foreground">No events match this filter.</div>
          </div>
         ) : (
          <div className="divide-y divide-border">
            {visible.map(r => (
              <div key={r.id} className="px-5 py-3 flex items-center gap-3 text-sm">
                <Badge variant="outline" className={`text-[10px] font-mono ${ACTION_STYLES[r.action] || "bg-muted"}`}>{r.action}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.actor_email || "system"}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {r.entity_type && r.entity_id ? `${r.entity_type} · ${r.entity_id.slice(0, 8)}…` : "—"}
                    {r.meta && Object.keys(r.meta).length > 0 && ` · ${Object.entries(r.meta).map(([k,v]) => `${k}=${v}`).join(", ")}`}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTimeFull(r.at)}</div>
              </div>
            ))}
          </div>
         )}
      </CardContent></Card>

      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => load(true)}
            disabled={loadingMore}
            data-testid="audit-load-more"
            className="gap-1.5"
          >
            {loadingMore ? "Loading…" : `Load more (${total - rows.length} remaining)`}
          </Button>
        </div>
      )}
    </div>
  );
}
